import axios from 'axios'
import { format, isValid } from 'date-fns'
import clsx, { ClassValue } from 'clsx'
import { twMerge } from 'tailwind-merge'

import { APP_TITLE } from '$services/environments'
import { configUpload } from '$services/api'
import {
  ALLOWED_SPECIAL_CHARACTERS,
  CF_TURNSTILE_HEADER_KEY,
  DATE_FORMAT_WITH_TIMEZONE,
  TOOLTIP_ID,
  THEME_NAME
} from '$app/constants/common'
import { isString } from 'lodash-es'

export const setLocalStorage = <T>(key: string, value: T) => {
  if (typeof value === 'function') {
    console.warn('Functions cannot be stored in localStorage')
    return
  }

  localStorage.setItem(key, JSON.stringify(value))
}

export const getLocalStorage = <T>(key: string): T | undefined => {
  const storedValue = localStorage.getItem(key)

  try {
    return storedValue ? JSON.parse(storedValue) : undefined
  } catch (error) {
    return undefined
  }
}

export const removeLocalStorage = (key: string) => {
  localStorage.removeItem(key)
}

export type TypeAppTitle = {
  title?: string
  suffix?: string
  addSuffix?: boolean
  returnAppTitle?: boolean
}

export const getAppTitle = (options: TypeAppTitle = {}): string => {
  const {
    title,
    suffix = APP_TITLE,
    addSuffix = true,
    returnAppTitle = true
  } = options

  if (title === undefined && returnAppTitle) {
    return APP_TITLE
  }

  if (addSuffix) {
    return `${title} - ${suffix}`
  }

  return title || ''
}

export const cn = (...inputs: ClassValue[]) => twMerge(clsx(inputs))

export type TUploadFileData = {
  file_name: string
  key: string
  file: File
  preview: string
}

export const uploadFile = async (
  file: File,
  token?: string
): Promise<TUploadFileData> => {
  const { url, key, preview } = await configUpload(token)

  await axios.put(url, file, {
    headers: {
      'Content-Type': file.type,
      Accept: 'application/json'
    }
  })

  return {
    file_name: file.name.split('.').slice(0, -1).join('.'), // remove extension
    key,
    file,
    preview
  }
}

export const formatFileSize = (size: number) => {
  // return '0 B' for null, undefined, negative or zero values
  if (size === null || size === undefined || size <= 0) {
    return '0 B'
  }

  const fileSizeUnits = ['B', 'KB', 'MB', 'GB', 'TB', 'PB']

  let unitIndex = 0

  /**
   * Divide size by 1024 until it's less than 1024 to identify the appropriate unit
   */
  while (size >= 1024) {
    size /= 1024
    unitIndex++
  }

  return `${+size.toFixed(2)} ${fileSizeUnits[unitIndex]}`
}

export const getFlagUrl = (code: string) => {
  return `https://flagcdn.com/${code.toLocaleLowerCase()}.svg`
}

export const getPhone = ({
  phoneCode,
  phoneNo
}: {
  phoneCode?: string
  phoneNo?: string
}) => {
  if (!phoneCode && !phoneNo) return '-'

  const phoneNoParts = [phoneCode, phoneNo]
  const parsed = phoneNoParts
    .map(v => (v ? v.replaceAll('N/A', '') : ''))
    .filter(Boolean)

  return parsed.join(' ')
}

export const getDateOfBirth = (date?: string) => {
  if (!date) return '-'

  return isValid(new Date(date)) ? format(date, 'dd MMM yyyy') : date
}

export const formatNumber = (value: number) => {
  if (isNaN(value)) {
    console.warn('NaN value was provided')
    return '-'
  }

  return value.toLocaleString()
}

export const getRandomChar = (chars: string) =>
  chars[Math.floor(Math.random() * chars.length)]

export const shuffleArray = <T>(arr: T[]) => {
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[arr[i], arr[j]] = [arr[j], arr[i]]
  }
  return arr
}

export const shuffleString = (str: string) => {
  const splitStrArr = str.split('')
  for (let i = splitStrArr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[splitStrArr[i], splitStrArr[j]] = [splitStrArr[j], splitStrArr[i]]
  }
  return splitStrArr.join('')
}

export const generatePassword = (pwdLength: number) => {
  const lowercase = 'abcdefghijklmnopqrstuvwxyz'
  const uppercase = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'
  const numbers = '0123456789'
  const specialChars = ALLOWED_SPECIAL_CHARACTERS.join('')

  const category = shuffleArray([lowercase, uppercase, numbers, specialChars])

  let str = ''

  for (let i = 0; i < pwdLength; i++) {
    const idx = i % category.length
    const randomChar = getRandomChar(category[idx])
    str += randomChar
  }

  return shuffleString(str)
}

export const copy = (value: string, callBackFn?: VoidFunction) => {
  // Create a Blob for the HTML content
  const htmlBlob = new Blob([value], { type: 'text/html' })
  // Create a Blob for the plain text content
  const textBlob = new Blob([value], { type: 'text/plain' })

  // Create a ClipboardItem with the appropriate MIME types
  const clipboardItem = new ClipboardItem({
    'text/html': htmlBlob,
    'text/plain': textBlob
  })

  navigator.clipboard.writeText(value)

  navigator.clipboard.write([clipboardItem]).then(callBackFn)
}

export const tooltipProps = (value: string, tooltipId = TOOLTIP_ID) => {
  return {
    'data-tooltip-id': tooltipId,
    'data-tooltip-content': value
  }
}

/**
 * Skipped tests for formatDateWithGMT:
 * - Simple wrapper around date-fns format function
 * - Tests would be brittle due to timezone differences
 * - Low value/effort ratio for testing a 3-line function
 * - date-fns library itself is already well-tested
 */
export const formatDateWithGMT = (value?: string) =>
  value ? format(value, DATE_FORMAT_WITH_TIMEZONE) : '-'

export const isDigitsOnly = (value: string) => {
  if (!value || !isString(value)) return false

  const numberRegex = /^\d+$/
  return numberRegex.test(value)
}

export const formatTime = (value: number) => value.toString().padStart(2, '0')

export const pluralize = (count: number | undefined, word: string) =>
  `${formatNumber(count ?? 0)} ${word}${count && count > 1 ? 's' : ''}`

export const getCFTurnstileHeader = (cf_turnstile_token?: string) =>
  cf_turnstile_token
    ? { [CF_TURNSTILE_HEADER_KEY]: cf_turnstile_token }
    : undefined

export const removeDashWithSpace = (value?: string) =>
  value ? value.replaceAll('-', ' ') : '-'

export type PluralizedText = { singular: string; plural: string }
export const pluralizeDeviceLogin = (count: number, text: PluralizedText) => {
  return count > 1 ? text.plural : text.singular
}

export const getThemedData = <T>(data: Record<typeof THEME_NAME, T>) => {
  return data[THEME_NAME]
}
